/**
 * @fileoverview Issue classification rubric for critique and continuity agents.
 *
 * Provides a unified taxonomy for classifying issues found during chapter analysis.
 * This includes severity levels, confidence levels, domain categories, and scope definitions.
 * All critique and continuity agents should use these definitions to ensure consistent
 * classification across the system.
 */

import { z } from 'zod';

// =============================================================================
// SEVERITY LEVELS
// =============================================================================

/**
 * Severity levels indicate how urgently an issue needs to be addressed.
 * Simplified to 3 levels for clearer prioritization.
 */
export const ISSUE_SEVERITIES = [
  {
    key: 'must_fix',
    label: 'Must Fix',
    description: 'Issue that breaks reader comprehension, immersion, or story logic. Cannot be shipped as-is.',
    examples: [
      'Character uses knowledge they cannot have',
      'Dead character appears alive without explanation',
      'Scene contradicts established timeline',
      'Dialogue is incomprehensible or breaks character completely',
    ],
  },
  {
    key: 'should_fix',
    label: 'Should Fix',
    description: "Issue that noticeably reduces quality but doesn't break the story. Most readers will notice.",
    examples: [
      'Dialogue feels unnatural for the character',
      'Pacing drags in a scene without purpose',
      'Sensory details missing in key moment',
      'Minor continuity error that attentive readers will catch',
    ],
  },
  {
    key: 'could_fix',
    label: 'Could Fix',
    description: 'Polish-level issue. Only attentive readers notice. Fix if time permits.',
    examples: [
      'Slight word repetition in nearby paragraphs',
      'Transition could be smoother',
      'Minor inconsistency in descriptive detail',
      'Dialogue tag could be more varied',
    ],
  },
] as const;

export type IssueSeverity = (typeof ISSUE_SEVERITIES)[number]['key'];

export const IssueSeverityEnum = z.enum(['must_fix', 'should_fix', 'could_fix']);

// =============================================================================
// CONFIDENCE LEVELS
// =============================================================================

/**
 * Confidence levels indicate how certain the agent is about the issue.
 * Used for post-processing filtering based on severity thresholds.
 */
export const ISSUE_CONFIDENCE_LEVELS = [
  {
    key: 'certain',
    label: 'Certain',
    description: 'Objective issue - factual error, clear contradiction, or violation of established rules.',
    reportingGuideline: 'Always report regardless of severity.',
  },
  {
    key: 'likely',
    label: 'Likely',
    description: 'Probable issue - strong evidence suggests a problem, but could be intentional.',
    reportingGuideline: 'Report for must_fix and should_fix severity.',
  },
  {
    key: 'possible',
    label: 'Possible',
    description: 'Subjective concern - depends on reader interpretation or authorial intent.',
    reportingGuideline: 'Only report for must_fix severity.',
  },
] as const;

export type IssueConfidence = (typeof ISSUE_CONFIDENCE_LEVELS)[number]['key'];

export const IssueConfidenceEnum = z.enum(['certain', 'likely', 'possible']);

// =============================================================================
// DOMAIN TAXONOMY
// =============================================================================

/**
 * Domains group related categories and map to specific agents.
 * Each agent should only output categories from its assigned domains.
 */
export const ISSUE_DOMAINS = [
  {
    key: 'continuity',
    label: 'Continuity',
    description: 'Facts, timeline, and consistency with established story elements.',
    categories: [
      { key: 'timeline', label: 'Timeline', description: 'Events happening in wrong order or impossible timeframes' },
      { key: 'character_knowledge', label: 'Character Knowledge', description: 'Character knows something they should not or forgets something they should know' },
      { key: 'character_state', label: 'Character State', description: 'Physical or emotional state contradicts what was established' },
      { key: 'world_rules', label: 'World Rules', description: 'Violation of established world-building rules or magic systems' },
      { key: 'object_state', label: 'Object State', description: 'Item location, condition, or existence contradicts established facts' },
      { key: 'setting', label: 'Setting', description: 'Location details contradict established geography or descriptions' },
    ],
    agents: [
      'character-continuity-checker-v2',
      'chapter-continuity-checker-v2',
      'item-continuity-checker-v2',
    ],
  },
  {
    key: 'craft',
    label: 'Craft',
    description: 'Writing quality, prose mechanics, and stylistic execution.',
    categories: [
      { key: 'dialogue', label: 'Dialogue', description: 'Issues with dialogue naturalness, voice consistency, or attribution' },
      { key: 'prose_rhythm', label: 'Prose Rhythm', description: 'Sentence flow, pacing within paragraphs, and transitions' },
      { key: 'voice', label: 'Voice', description: 'Narrative voice consistency and POV adherence' },
      { key: 'show_vs_tell', label: 'Show vs Tell', description: 'Over-explanation or telling emotions instead of showing' },
      { key: 'sensory', label: 'Sensory Detail', description: 'Missing or imbalanced sensory imagery' },
      { key: 'atmosphere', label: 'Atmosphere', description: 'Mood and tone not matching scene intent' },
    ],
    agents: [
      'critique-dialogue',
      'critique-prose-flow',
      'critique-sensory-atmosphere',
      'critique-voice-emotion',
    ],
  },
  {
    key: 'clarity',
    label: 'Clarity',
    description: 'Reader comprehension - confusing passages, unclear references, ambiguous meaning.',
    categories: [
      { key: 'ambiguous_reference', label: 'Ambiguous Reference', description: 'Unclear pronoun or reference that could confuse readers' },
      { key: 'confusing_sequence', label: 'Confusing Sequence', description: 'Action or event sequence hard to follow' },
      { key: 'unclear_motivation', label: 'Unclear Motivation', description: 'Character action lacks clear reasoning' },
    ],
    agents: ['*'], // All agents can report clarity issues
  },
  {
    key: 'engagement',
    label: 'Engagement',
    description: 'Reader experience - pacing, tension, emotional resonance.',
    categories: [
      { key: 'pacing', label: 'Pacing', description: 'Scene moves too fast or too slow for its purpose' },
      { key: 'tension', label: 'Tension', description: 'Stakes or conflict not adequately maintained' },
      { key: 'emotional_beat', label: 'Emotional Beat', description: 'Emotional moment not landing with impact' },
    ],
    agents: [
      'critique-prose-flow',
      'critique-voice-emotion',
    ],
  },
] as const;

export type IssueDomain = (typeof ISSUE_DOMAINS)[number]['key'];

export const IssueDomainEnum = z.enum(['continuity', 'craft', 'clarity', 'engagement']);

/**
 * Flat list of all category keys for schema validation.
 */
export const ALL_CATEGORY_KEYS = ISSUE_DOMAINS.flatMap(d => d.categories.map(c => c.key));

export const IssueCategoryEnum = z.enum([
  'timeline',
  'character_knowledge',
  'character_state',
  'world_rules',
  'object_state',
  'setting',
  'dialogue',
  'prose_rhythm',
  'voice',
  'show_vs_tell',
  'sensory',
  'atmosphere',
  'ambiguous_reference',
  'confusing_sequence',
  'unclear_motivation',
  'pacing',
  'tension',
  'emotional_beat',
]);

export type IssueCategory = z.infer<typeof IssueCategoryEnum>;

// =============================================================================
// SCOPE DEFINITIONS (Findings vs Weaknesses)
// =============================================================================

/**
 * Scope definitions distinguish between localized issues (findings)
 * and systemic patterns (weaknesses).
 */
export const ISSUE_SCOPE_DEFINITIONS = {
  finding: {
    label: 'Finding',
    description: 'A localized issue that can be fixed by editing a single paragraph.',
    requirements: [
      'Must include the full paragraph as excerpt (direct quote from the text)',
      'Description must be specific enough to fix within the excerpt paragraph',
      'If the same issue appears in multiple paragraphs, report as weakness instead',
    ],
  },
  weakness: {
    label: 'Weakness',
    description: 'A systemic pattern spanning multiple paragraphs with no single fix location.',
    requirements: [
      'Describe the pattern, not individual instances',
      'Do not include excerpts (too broad to localize)',
      'Examples: "All characters use similar vocabulary", "Emotional beats consistently rushed"',
    ],
  },
} as const;

// =============================================================================
// SHARED SCHEMAS
// =============================================================================

/**
 * Schema for a single finding (localized issue).
 */
export const FindingSchema = z.object({
  severity: IssueSeverityEnum.describe(
    'How severe is this issue? must_fix=breaks story/comprehension, should_fix=noticeably reduces quality, could_fix=polish-level'
  ),
  confidence: IssueConfidenceEnum.describe(
    'How certain are you about this issue? certain=objective error, likely=probable issue, possible=subjective concern'
  ),
  category: IssueCategoryEnum.describe(
    'The specific category of this finding from the unified taxonomy'
  ),
  description: z.string().describe(
    'Clear explanation of the issue. Be specific enough that the fix can be made within the excerpt paragraph.'
  ),
  excerpt: z.string().describe(
    'The full paragraph from the text where this issue occurs. Always quote the complete paragraph, not just a phrase or sentence.'
  ),
  suggestion: z.string().optional().describe(
    'Specific suggestion for how to address this issue within the paragraph'
  ),
});

export type Finding = z.infer<typeof FindingSchema>;

/**
 * Schema for a weakness (systemic pattern).
 */
export const WeaknessSchema = z.object({
  severity: IssueSeverityEnum.describe(
    'How severe is this pattern? must_fix=significantly hurts the story, should_fix=noticeably reduces quality, could_fix=polish-level'
  ),
  confidence: IssueConfidenceEnum.describe(
    'How certain are you about this pattern? certain=clearly demonstrable, likely=probable pattern, possible=subjective observation'
  ),
  domain: IssueDomainEnum.describe(
    'The broad domain this weakness falls under'
  ),
  description: z.string().describe(
    'Description of the systemic pattern. Describe the pattern itself, not individual instances.'
  ),
});

export type Weakness = z.infer<typeof WeaknessSchema>;

/**
 * Unified output schema for all evaluation agents (critique and continuity).
 */
export const EvaluationOutputSchema = z.object({
  commentary: z.string().describe(
    'Brief summary of findings and overall assessment (2-3 sentences). ' +
    'This will be recorded in the drafting history.'
  ),
  rating: z.number().min(1).max(10).describe(
    'Overall quality rating from 1-10 for the aspect this agent evaluates'
  ),
  findings: z.array(FindingSchema).describe(
    'Localized issues that can be fixed by editing a single paragraph. Each finding must include the full paragraph as an excerpt.'
  ),
  strengths: z.array(z.string()).min(4).max(5).describe(
    '4-5 specific things the draft does well that should be preserved during revision'
  ),
  weaknesses: z.array(WeaknessSchema).min(2).max(3).describe(
    '2-3 systemic patterns that require changes across multiple paragraphs.'
  ),
});

export type EvaluationOutput = z.infer<typeof EvaluationOutputSchema>;

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get the allowed categories for a specific agent.
 */
export function getAllowedCategoriesForAgent(agentName: string): IssueCategory[] {
  const categories: IssueCategory[] = [];

  for (const domain of ISSUE_DOMAINS) {
    // Cast to string[] to avoid readonly tuple type issues
    const agents = domain.agents as readonly string[];
    if (agents.includes(agentName) || agents.includes('*')) {
      categories.push(...domain.categories.map(c => c.key as IssueCategory));
    }
  }

  return categories;
}

/**
 * Get the domain for a specific category.
 */
export function getDomainForCategory(category: IssueCategory): IssueDomain | undefined {
  for (const domain of ISSUE_DOMAINS) {
    if (domain.categories.some(c => c.key === category)) {
      return domain.key as IssueDomain;
    }
  }
  return undefined;
}

/**
 * Filter findings based on confidence thresholds.
 * - certain: always included
 * - likely: included for must_fix and should_fix
 * - possible: only included for must_fix
 */
export function filterByConfidenceThreshold(findings: Finding[]): Finding[] {
  return findings.filter(finding => {
    if (finding.confidence === 'certain') return true;
    if (finding.confidence === 'likely') {
      return finding.severity === 'must_fix' || finding.severity === 'should_fix';
    }
    if (finding.confidence === 'possible') {
      return finding.severity === 'must_fix';
    }
    return false;
  });
}

/**
 * Filter weaknesses based on confidence thresholds.
 * Same logic as findings.
 */
export function filterWeaknessesByConfidenceThreshold(weaknesses: Weakness[]): Weakness[] {
  return weaknesses.filter(weakness => {
    if (weakness.confidence === 'certain') return true;
    if (weakness.confidence === 'likely') {
      return weakness.severity === 'must_fix' || weakness.severity === 'should_fix';
    }
    if (weakness.confidence === 'possible') {
      return weakness.severity === 'must_fix';
    }
    return false;
  });
}
